import { useEffect, useRef, useState, type HTMLAttributes } from 'react';
import {
  AlignLeft,
  CheckSquare,
  ChevronDown,
  GripVertical,
  Hash,
  ListChecks,
  Mail,
  Phone,
  Plus,
  ToggleLeft,
  Trash2,
  Type,
  X,
} from 'lucide-react';
import { cn } from '@/utils/helpers';
import type { LandingPageQuestion, QuestionType } from '@/types';

interface QuestionEditorProps {
  question: LandingPageQuestion;
  dragHandleProps?: HTMLAttributes<HTMLElement>;
  onChange: (updated: LandingPageQuestion) => void;
  onDelete: () => void;
}

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  short_text: 'Short text',
  long_text: 'Long text / paragraph',
  email: 'Email address',
  phone: 'Phone number',
  number: 'Number',
  select: 'Single choice',
  multi_select: 'Multiple choice',
  yes_no: 'Yes / No',
};

const QUESTION_TYPES = Object.entries(QUESTION_TYPE_LABELS) as Array<[QuestionType, string]>;

const TYPE_ICONS = {
  short_text: Type,
  long_text: AlignLeft,
  email: Mail,
  phone: Phone,
  number: Hash,
  select: ListChecks,
  multi_select: CheckSquare,
  yes_no: ToggleLeft,
} satisfies Record<QuestionType, typeof Type>;

function supportsOptions(type: QuestionType) {
  return type === 'select' || type === 'multi_select';
}

export default function QuestionEditor({
  question,
  dragHandleProps,
  onChange,
  onDelete,
}: QuestionEditorProps) {
  const [expanded, setExpanded] = useState(question.label.trim() === '');
  const [focusLastOption, setFocusLastOption] = useState(false);
  const lastOptionRef = useRef<HTMLInputElement | null>(null);
  const TypeIcon = TYPE_ICONS[question.type];
  const options = question.options ?? [];

  useEffect(() => {
    if (!focusLastOption) return;
    lastOptionRef.current?.focus();
    setFocusLastOption(false);
  }, [focusLastOption, options.length]);

  const update = (patch: Partial<LandingPageQuestion>) => {
    onChange({ ...question, ...patch });
  };

  const updateType = (type: QuestionType) => {
    onChange({
      ...question,
      type,
      options: supportsOptions(type) ? question.options ?? [''] : undefined,
    });
  };

  const updateOption = (index: number, value: string) => {
    onChange({
      ...question,
      options: options.map((option, optionIndex) => (optionIndex === index ? value : option)),
    });
  };

  const addOption = () => {
    onChange({ ...question, options: [...options, ''] });
    setFocusLastOption(true);
  };

  const removeOption = (index: number) => {
    onChange({
      ...question,
      options: options.filter((_, optionIndex) => optionIndex !== index),
    });
  };

  return (
    <div className="rounded-lg border border-navy-border bg-navy-card">
      <div
        className="flex cursor-pointer items-center gap-3 px-4 py-3"
        onClick={() => setExpanded(true)}
      >
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-navy-hover hover:text-ink-primary"
          aria-label="Drag question"
          onClick={(event) => event.stopPropagation()}
          {...dragHandleProps}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-navy/70 text-indigo-300">
          <TypeIcon className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-ink-primary">
            {question.label.trim() || 'Untitled question'}
          </div>
          <div className="mt-0.5 text-xs text-ink-muted">
            {QUESTION_TYPE_LABELS[question.type]}
            {question.required ? ' - Required' : ''}
          </div>
        </div>

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-navy-hover hover:text-ink-primary"
          aria-label={expanded ? 'Collapse question' : 'Expand question'}
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
        </button>

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-red-500/10 hover:text-red-400"
          aria-label="Delete question"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-navy-border px-4 py-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-secondary" htmlFor={`question-label-${question.id}`}>
              Label
            </label>
            <input
              id={`question-label-${question.id}`}
              value={question.label}
              onChange={(event) => update({ label: event.target.value })}
              className="h-10 w-full rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-secondary" htmlFor={`question-helper-${question.id}`}>
              Helper text
            </label>
            <input
              id={`question-helper-${question.id}`}
              value={question.helper ?? ''}
              onChange={(event) => update({ helper: event.target.value || undefined })}
              placeholder="Optional hint shown below the field"
              className="h-10 w-full rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-secondary" htmlFor={`question-type-${question.id}`}>
                Type
              </label>
              <select
                id={`question-type-${question.id}`}
                value={question.type}
                onChange={(event) => updateType(event.target.value as QuestionType)}
                className="h-10 w-full rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary focus:outline-none focus:ring-2 focus:ring-gold/40"
              >
                {QUESTION_TYPES.map(([type, label]) => (
                  <option key={type} value={type}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <label className="mt-7 flex h-10 items-center gap-3 rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary">
              <input
                type="checkbox"
                checked={question.required}
                onChange={(event) => update({ required: event.target.checked })}
                className="h-4 w-4"
              />
              Required
            </label>
          </div>

          {supportsOptions(question.type) && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-ink-secondary">Options</div>
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    ref={index === options.length - 1 ? lastOptionRef : undefined}
                    value={option}
                    onChange={(event) => updateOption(index, event.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="h-9 flex-1 rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-red-500/10 hover:text-red-400"
                    aria-label="Remove option"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addOption}
                className="inline-flex h-9 items-center gap-2 rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-secondary transition-colors hover:bg-navy-hover hover:text-ink-primary"
              >
                <Plus className="h-4 w-4" />
                Add option
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
